/**
 * Joins a property's city and address without repeating the city.
 *
 * `AddressSuggest` writes the whole geocoder line into `address` and stores
 * the locality separately in `city`. That line is not city-first outside the
 * federal cities: Yandex returns "Московская область, Химки, улица X, 1" with
 * city "Химки", so a leading-prefix check finds no match and a plain join
 * renders the city twice — in lists, deal cards, invoices, contracts, the
 * Telegram keyboard and the Avito feed. Manually typed addresses usually carry
 * no city at all, so the city still has to be prefixed in that case.
 *
 * The city is therefore matched per comma-separated segment rather than only
 * at the start, and a segment equal to the city is dropped wherever it sits.
 * Matching is case-insensitive and tolerates the "г." / "город" prefixes the
 * geocoder sometimes emits, but requires the whole segment to match — so
 * "Москва" never swallows the "Москва" inside "Московская область".
 *
 * Kept in step with `formatPropertyAddress` in
 * `packages/common/lib/utils/property-address.ts`: server-composed titles must
 * produce the same string this renders.
 */

const normalizeSegment = (segment: string): string =>
	segment
		.trim()
		.toLowerCase()
		.replace(/^(?:г\.|г|город|гор\.)\s+/u, '')

export function formatPropertyAddress(city?: string | null, address?: string | null): string {
	const a = address?.trim() ?? ''
	const c = city?.trim() ?? ''
	if (!c) return a
	if (!a) return c

	const wanted = normalizeSegment(c)
	const kept = a
		.split(',')
		.map((segment) => segment.trim())
		.filter((segment) => segment && normalizeSegment(segment) !== wanted)

	// Every segment was the city ("Москва" stored as both city and address):
	// the city alone is the whole answer.
	if (!kept.length) return c

	return `${c}, ${kept.join(', ')}`
}

/**
 * The address suggester writes the street address into `address`, but the flat
 * number is a separate column — an address built from city+address alone sends
 * someone to the entrance and no further. Shared so every consumer (meeting
 * notices, document wizard) prints the same line for the same property.
 */
/**
 * How the number is spelled depends on what is being let. The column is one —
 * `apartmentNumber` — but «кв. 5» on a warehouse would put a flat into a
 * commercial lease, and this line goes into contracts verbatim.
 */
const UNIT_SHORT: Record<string, string> = {
	OFFICE:    'properties.card.unitShort',
	RETAIL:    'properties.card.unitShort',
	WAREHOUSE: 'properties.card.unitShort',
	COWORKING: 'properties.card.unitShort',
	GARAGE:    'properties.card.garageShort',
}

export function propertyAddressWithUnit(
	p: { city: string; address: string; apartmentNumber?: string; type?: string },
	t: (key: string, opts?: Record<string, unknown>) => string,
): string {
	const base = formatPropertyAddress(p.city, p.address)
	const unit = p.apartmentNumber?.trim()
	if (!unit) return base
	const key = (p.type && UNIT_SHORT[p.type]) ?? 'properties.card.apartmentShort'
	return `${base}, ${t(key, { number: unit })}`
}
